'use client';

import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import logOneTapDisplayed from '@alltrails/amplitude/events/logOneTapDisplayed';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import useUser from '@alltrails/context/hooks/useUser';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import useIsAndroid from '@alltrails/core/hooks/useIsAndroid';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import InstallMiniVariantInterstitial from '@alltrails/install-prompt/components/InstallMiniVariantInterstitial';
import { InstallBannerState } from '@alltrails/install-prompt/slices/installBanner';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import useGoogleHandler from '@alltrails/user-auth/hooks/useGoogleHandler';
import type LoginResponse from '@alltrails/user-auth/types/LoginResponse';
import styles from './GoogleOneTap.module.scss';

const GenericPostAuthUpsellModal = dynamic(() => import('@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellModal'), {
  ssr: false
});

type Props = {
  pageViewLimitReached?: boolean;
};

export default function GoogleOneTap({ pageViewLimitReached }: Props) {
  const googleHandler = useGoogleHandler();
  const setUser = useSetUser();
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const isAndroid = useIsAndroid();
  const [isMounted, setIsMounted] = useState(false);
  const isInstallBannerOpen = useSelector((state: { installBanner: InstallBannerState }) => state.installBanner?.isOpen);
  const [didAuthWithOneTap, setDidAuthWithOneTap] = useState(false);
  const [didShowUpsell, setDidShowUpsell] = useState(false);
  const dispatch = useDispatch();

  function getShouldShowOneTap() {
    return isMounted && !user && (!isMobile || isAndroid);
  }

  const shouldShowOneTap = getShouldShowOneTap();

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isMiniInterstitialOpen, setIsMiniInterstitialOpen] = useState(false);
  const [didShowInterstitial, setDidShowInterstitial] = useState(false);
  const [interstitialClosed, setInterstitialClosed] = useState(false);

  // Get trail ID from map state for deeplink context
  const trailId = useSelector((state: { map?: { trailId?: number } }) => state.map?.trailId);

  // Construct deeplinkData based on available page context
  const deeplinkData: DeeplinkData | undefined = useMemo(() => {
    if (trailId) {
      return { pageId: trailId, pageType: 'trail' };
    }
    return undefined;
  }, [trailId]);

  async function callback(resp: any) {
    try {
      const response: LoginResponse = await googleHandler({ googleUserOrToken: resp.credential, handlerType: 'login', oneTap: true });
      setUser(response.user);
      dispatch(setPlans(response.plans));
      dispatch(setCurrencyCode(response.currencyCode));

      // Desktop upsell flow
      if (!isMobile && response.user.subscriptionTier === 'base') {
        setIsUpsellModalOpen(true);
      }

      // Mobile Android interstitial flow - show interstitial before upsell
      if (isMobile && isAndroid) {
        setIsMiniInterstitialOpen(true);
        setDidShowInterstitial(true);
      }

      setDidAuthWithOneTap(true);
      window.google?.accounts.id.cancel(); // google can be undefined on mobile web
    } catch {
      // we don't have a design spec for showing errors so we ignore them for now
    }
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Mobile upsell flow
    // If an interstitial was shown, only show upsell after it's closed
    // If no interstitial, show upsell after banner is closed (fallback to existing behavior)
    const isBannerClosedOrUninitialized = !isInstallBannerOpen || isInstallBannerOpen === 'uninitialized';
    const interstitialConditionMet = didShowInterstitial ? interstitialClosed : isBannerClosedOrUninitialized;
    const isInUpsellTier = user?.subscriptionTier === 'base';
    const isEligibleForUpsell = isMobile && interstitialConditionMet && didAuthWithOneTap && !didShowUpsell && isInUpsellTier;

    if (isEligibleForUpsell) {
      setIsUpsellModalOpen(true);
      setDidShowUpsell(true);
    }
  }, [isInstallBannerOpen, didAuthWithOneTap, didShowUpsell, isMobile, user?.subscriptionTier, didShowInterstitial, interstitialClosed]);

  return (
    <>
      {shouldShowOneTap && (
        <>
          <div id="g_one_tap" className={styles.one} />
          <Script
            async
            onLoad={() => {
              window.google.accounts.id.initialize({
                callback,
                cancel_on_tap_outside: false,
                client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                prompt_parent_id: 'g_one_tap',
                use_fedcm_for_prompt: true
              });

              if (!pageViewLimitReached) {
                window.google.accounts.id.prompt((notification: any) => {
                  if (notification.isDisplayMoment() && notification.isDisplayed()) {
                    logOneTapDisplayed();
                  }
                });
              }
            }}
            src="https://accounts.google.com/gsi/client"
          />
        </>
      )}
      {isUpsellModalOpen && (
        <GenericPostAuthUpsellModal
          isOpen={isUpsellModalOpen}
          onCloseRequest={() => setIsUpsellModalOpen(false)}
          upsellTrigger="Complete Authentication"
        />
      )}
      <InstallMiniVariantInterstitial
        isOpen={isMiniInterstitialOpen}
        onRequestClose={() => {
          setIsMiniInterstitialOpen(false);
          setInterstitialClosed(true);
        }}
        promptLocation="trail page"
        displayTrigger="Complete Authentication"
        deeplinkData={deeplinkData}
      />
    </>
  );
}
